import { m } from "framer-motion";
import { useState } from "react";
import { Mail, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/contexts/LanguageContext";

const WAITLIST_ID = "cmogxur6w00pe01qtpr8e17v5";
const API_URL = `https://api.freewaitlists.com/waitlists/${WAITLIST_ID}`;

const WaitlistSection = () => {
  const { t, language } = useLanguage();
  const [email, setEmail] = useState("");
  const [interest, setInterest] = useState<"android" | "ios">("android");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const labels = {
    placeholder: language === "de" ? "E-Mail-Adresse eingeben" : "Enter your email",
    button: language === "de" ? "Warteliste beitreten" : "Join Waitlist",
    success:
      language === "de"
        ? "Erfolgreich eingetragen! Wir melden uns, sobald es losgeht."
        : "You're on the list! We'll be in touch as soon as we launch.",
    invalidEmail:
      language === "de"
        ? "Bitte gib eine gültige E-Mail-Adresse ein."
        : "Please enter a valid email address.",
    genericError:
      language === "de"
        ? "Etwas ist schiefgelaufen. Bitte versuche es erneut."
        : "Something went wrong. Please try again.",
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === "loading") return;

    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setStatus("error");
      setErrorMsg(labels.invalidEmail);
      return;
    }

    setStatus("loading");
    setErrorMsg(null);

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: trimmed,
          meta: { source: "freshkeeper.de", interest },
        }),
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      setStatus("success");
      setEmail("");
    } catch (err) {
      console.error("Waitlist signup failed", err);
      setStatus("error");
      setErrorMsg(labels.genericError);
    }
  };

  return (
    <section
      id="download"
      className="py-24 lg:py-32 bg-gradient-to-br from-fresh-green to-leaf relative overflow-hidden"
    >
      <div className="container relative z-10">
        <div className="max-w-2xl mx-auto text-center">
          <m.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-primary-foreground mb-4">
              {t("waitlist.title")}
            </h2>
            <p className="text-lg text-primary-foreground/80 mb-6">{t("waitlist.subtitle")}</p>
            <div className="mb-8 flex flex-wrap justify-center gap-3" aria-label={language === "de" ? "Plattform wählen" : "Choose platform"}>
              <button type="button" onClick={() => setInterest("android")} aria-pressed={interest === "android"} className={`rounded-full border px-5 py-2 text-sm font-medium transition-colors ${interest === "android" ? "border-primary-foreground bg-primary-foreground text-foreground" : "border-primary-foreground/50 text-primary-foreground hover:bg-primary-foreground/10"}`}>
                {language === "de" ? "Android vormerken" : "Notify me about Android"}
              </button>
              <button type="button" onClick={() => setInterest("ios")} aria-pressed={interest === "ios"} className={`rounded-full border px-5 py-2 text-sm font-medium transition-colors ${interest === "ios" ? "border-primary-foreground bg-primary-foreground text-foreground" : "border-primary-foreground/50 text-primary-foreground hover:bg-primary-foreground/10"}`}>
                {language === "de" ? "iOS vormerken" : "Notify me about iOS"}
              </button>
            </div>
          </m.div>

          <m.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="bg-card rounded-2xl shadow-card p-6 sm:p-8 text-left"
          >
            {status === "success" ? (
              <div className="flex flex-col items-center gap-3 py-4 text-center">
                <CheckCircle2 className="w-12 h-12 text-fresh-green" />
                <p className="text-base text-foreground font-medium">{labels.success}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
                  <Input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (status === "error") {
                        setStatus("idle");
                        setErrorMsg(null);
                      }
                    }}
                    placeholder={labels.placeholder}
                    aria-label={labels.placeholder}
                    className="pl-10 h-12"
                    disabled={status === "loading"}
                  />
                </div>
                <Button
                  type="submit"
                  size="lg"
                  variant="hero"
                  className="h-12 sm:w-auto w-full"
                  disabled={status === "loading"}
                >
                  {status === "loading" ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Mail className="w-5 h-5" />
                  )}
                  {labels.button}
                </Button>
              </form>
            )}

            {status === "error" && errorMsg && (
              <p className="text-sm text-destructive mt-3" role="alert">
                {errorMsg}
              </p>
            )}
            <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
              {language === "de"
                ? "Mit der Anmeldung werden deine E-Mail-Adresse und deine Plattformauswahl an FreeWaitlists übermittelt, damit wir dich zum Start informieren können. Du kannst die Löschung jederzeit per E-Mail an info@freshkeeper.de anfragen."
                : "When you join, your email address and platform preference are sent to FreeWaitlists so we can notify you about availability. You can request deletion at any time by emailing info@freshkeeper.de."}{" "}
              <a className="underline underline-offset-2" href={language === "de" ? "/datenschutz" : "/privacy-policy"}>{language === "de" ? "Unsere Datenschutzhinweise" : "Our privacy notice"}</a>{" · "}
              <a className="underline underline-offset-2" href="https://freewaitlists.com/privacy" target="_blank" rel="noopener noreferrer">FreeWaitlists Privacy</a>
            </p>
          </m.div>
        </div>
      </div>
    </section>
  );
};

export default WaitlistSection;
